import { useCallback, useEffect, useState } from 'react';
import { i18n } from '../i18n/i18n';
import { registerWebPushSubscription, unregisterWebPushSubscription } from '../services/apiService';
import { DevicePushState, getDevicePushState, registerSwAndSubscribe, unsubscribeDevice } from '../utils/webPush';
import { showToast } from '../components/kid/Toast';

/** Notifications on this device: current state plus turn on / turn off with friendly messages. */
export function useDevicePush() {
  const [device, setDevice] = useState<DevicePushState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getDevicePushState().then((s) => { if (alive) setDevice(s); });
    return () => { alive = false; };
  }, []);

  const turnOn = useCallback(async () => {
    setBusy(true);
    try {
      const sub = await registerSwAndSubscribe();
      if (!sub) {
        setDevice(await getDevicePushState());
        showToast({ message: i18n.t('my_settings.notif_blocked'), tone: 'error' });
        return;
      }
      await registerWebPushSubscription(sub, Intl.DateTimeFormat().resolvedOptions().timeZone);
      setDevice('on');
      showToast({ message: i18n.t('my_settings.notif_turned_on') });
    } catch (e: any) {
      showToast({ message: e?.message || 'Error', tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, []);

  const turnOff = useCallback(async () => {
    setBusy(true);
    try {
      await unregisterWebPushSubscription();
      await unsubscribeDevice();
      setDevice('off');
      showToast({ message: i18n.t('my_settings.notif_turned_off') });
    } catch (e: any) {
      showToast({ message: e?.message || 'Error', tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, []);

  return { device, busy, turnOn, turnOff };
}
